import moment from 'moment'
import { DefaultRoute } from '../router/routes'

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = (num) =>
  num > 999 ? `${(num / 1000).toFixed(1)}k` : num

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, '')

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value,
  formatting = { month: 'short', day: 'numeric', year: 'numeric' }
) => {
  if (!value) return value
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: 'short', day: 'numeric' }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' }
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem('userData')
export const getUserData = () => JSON.parse(localStorage.getItem('userData'))

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  if (userRole === 'admin') return DefaultRoute
  if (userRole === 'client') return '/access-control'
  return '/login'
}

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a', // for option hover bg-color
    primary: '#7367f0', // for selected option bg-color
    neutral10: '#7367f0', // for tags bg-color
    neutral20: '#ededed', // for input border-color
    neutral30: '#ededed', // for input hover border-color
  },
})

export const hexToBinary = (hex, bits) => {
  const binary = parseInt(hex, 16).toString(2).padStart(bits, '0')
  return binary
}

export const hexToDecimal = (hex) => {
  let stringHex = hex.toString()
  const decimal = parseInt(stringHex, 16)
  return decimal
}
export const decimalToBinary = (hex, binary) => {
  return hex.toString(2).padStart(binary, '0')
}

export const decimalToHex = (decimalNumber, binary) => {
  let intDecimal = parseInt(decimalNumber)
  let hexString = intDecimal.toString(16).padStart(binary, '0')
  return hexString
}

export const binaryToDecimal = (hex) => {
  const decimal = parseInt(hex, 2)
  return decimal
}

export const splitAndReverse = (str, length) => {
  if (length === 1) {
    return str.reverse()
  } else {
    str.match(/.{1,2}/g).reverse()
  }
}

export const splitIntoTwo = (str) => {
  return str.match(/.{1,2}/g)
}

export const creteSubstring = (str) => {
  return (substrings = str.match(/.{1,2}/g))
}

export const getEvenFromString = (str) => {
  return str.filter((_, index) => {
    return index % 2 === 0
  })
}

export const getOddFromString = (str) => {
  return str.filter((_, index) => {
    return index % 2 !== 0
  })
}

export const convertTo12HourFormat = (timeString) => {
  const [hours, minutes] = timeString.split(':')
  let hours12 = hours % 12
  const amOrPm = hours >= 12 ? 'PM' : 'AM'
  return `${hours12.toString().padStart(2, '0')}:${minutes} ${amOrPm}`
}

export const convertTimeTo12HourFormat = (hours, minutes) => {
  // Determine the AM/PM indicator
  const amOrPm = hexToDecimal(hours) >= 12 ? 'PM' : 'AM'

  // Convert hours to 12-hour format
  const hours12 = hexToDecimal(hours) % 12 || 12

  // Return the formatted time string
  return `${hours12}:${hexToDecimal(minutes)
    .toString()
    .padStart(2, '0')} ${amOrPm}`
}

export const rtcTimeToComputerTime = (junctionTime) => {
  return {
    hours: hexToDecimal(junctionTime[4]),
    minutes: hexToDecimal(junctionTime[5]),
    seconds: hexToDecimal(junctionTime[6]),
    date: hexToDecimal(junctionTime[0]),
    month: hexToDecimal(junctionTime[1]),
    year: hexToDecimal(junctionTime[2]),
    day: hexToDecimal(junctionTime[3]),
  }
}

export const computerTimeToRTC = (timestamp) => {
  const dateObj = new Date(timestamp)

  const year = dateObj.getFullYear()
  const month = ('0' + (dateObj.getMonth() + 1)).slice(-2)
  const date = ('0' + dateObj.getDate()).slice(-2)
  const day = ['1', '2', '3', '4', '5', '6', '7'][dateObj.getDay()]
  const hours = ('0' + dateObj.getHours()).slice(-2)
  const minutes = ('0' + dateObj.getMinutes()).slice(-2)

  return {
    year: decimalToHex(year.toString().slice(-2), 2),
    month: decimalToHex(month, 2),
    date: decimalToHex(date, 2),
    day: decimalToHex(day, 2),
    hours: decimalToHex(hours, 2),
    minutes: decimalToHex(minutes, 2),
  }
}

export const convertTimeTo24HourFormat = (timeString) => {
  const [time, amOrPm] = timeString.split(' ')

  let [hours, minutes] = time.split(':')
  hours = Number(hours)

  if (amOrPm === 'PM' && hours !== 12) {
    hours += 12
  } else if (amOrPm === 'AM' && hours === 12) {
    hours = 0
  }

  return {
    hours: hours.toString(16).padStart(2, '0'),
    minutes: Number(minutes).toString(16).padStart(2, '0'),
  }
}

export const concatHexToBinary = (str) => {
  const splittedStr = splitIntoTwo(str)
  const reversedAndBinary = []
  splittedStr.forEach((byte) => {
    reversedAndBinary.push(hexToBinary(byte, 8).split('').reverse().join(''))
  })

  return reversedAndBinary.join('').split('')
}
export const BinaryToHexConcat = (bits) => {
  const bytes = []
  for (let i = 0; i < bits.length; i += 8) {
    const byteBits = bits.slice(i, i + 8)
    const reversedByteBits = byteBits.reverse()
    const byte = parseInt(reversedByteBits.join(''), 2)
      .toString(16)
      .padStart(2, '0')
    bytes.push(byte)
  }
  return bytes.join('')
}

export const fetchPhase = (index) => {
  let phaseNumber = 0
  for (let i = 0; i < 10; i++) {
    if (
      concatHexToBinary(liveStatus.live_overview[`loop_phase${i + 1}`])
        .join('')
        .substring(index, index + 1) === '1'
    ) {
      phaseNumber = i
      break
    }
  }
  return phaseNumber + 1
}

export const getRandomHexColor = (existingColors) => {
  let lightness = 0
  let newColor
  do {
    newColor =
      '#' + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, '0')
    // Convert hex color to RGB values
    let red = parseInt(newColor.substring(1, 3), 16)
    let green = parseInt(newColor.substring(3, 5), 16)
    let blue = parseInt(newColor.substring(5, 7), 16)
    // Calculate lightness value using HSL color model
    let max = Math.max(red, green, blue)
    let min = Math.min(red, green, blue)
    lightness = (max + min) / 2 / 255
    // Exclude colors with low lightness values
  } while (existingColors.includes(newColor) || lightness < 0.5)
  return newColor
}

export const havePermission = (permissionArray, nameToFind) => {
  const isNameIncluded = permissionArray.some(
    (item) => item.name.toLowerCase() === nameToFind.toLowerCase()
  )
  return isNameIncluded
}

export const getPreviousMinutes = (minutes) => {
  return moment.utc().subtract(minutes, 'minutes').format('HH:mm:ss')
}

export const convertDateAndTimeToUTC = (localDateTimeStr) => {
  const dateObj = new Date(localDateTimeStr)

  // create a Moment.js object in local time
  const localDt = moment(dateObj)

  // convert to UTC
  const utcDt = localDt.utc()

  // format the UTC date as a string
  const utcDtStr = utcDt.format('YYYY-MM-DD HH:mm:ss')

  return utcDtStr
}

export const convertUTCDateToLocal = (utcDateStr) => {
  const utcDate = moment.utc(utcDateStr)

  // convert to local time using moment().local()
  const localDate = utcDate.local()

  // format local date string
  const localDateStr = localDate.format('MMM D, YYYY')

  return localDateStr
}

export const convertUTCTimeToLocal = (utcDateStr) => {
  const utcDate = moment.utc(utcDateStr)

  // convert to local time using moment().local()
  const localDate = utcDate.local()

  // format local date string
  const localDateStr = localDate.format('h:mm A')

  return localDateStr
}

export const convertUTCDateAndTimeToLocal = (utcDateStr) => {
  const utcDate = moment.utc(utcDateStr)

  // convert to local time using moment().local()
  const localDate = utcDate.local()

  // format local date string
  const localDateStr = localDate.format('MMM D, YYYY, h:mm A')

  return localDateStr
}

export const getGroups = (totalGroups) => {
  let groups = []
  for (let i = 0; i < totalGroups; i++) {
    groups.push({ label: `Group ${i + 1}`, value: i })
  }
  return groups
}

export const getFlashingGroups = (totalGroups) => {
  let groups = [{ label: 'Disable', value: 0 }]
  for (let i = 1; i <= totalGroups; i++) {
    groups.push({ label: `Group ${i}`, value: i })
  }
  return groups
}

export const getInputs = (totalInputs) => {
  let inputs = []
  for (let i = 0; i < totalInputs; i++) {
    inputs.push({ label: `Input ${i + 1}`, value: i })
  }
  return inputs
}

export const getChannels = (totalChannels) => {
  let channels = []
  for (let i = 0; i < totalChannels; i++) {
    channels.push({ label: `Channel ${i + 1}`, value: i })
  }
  return channels
}

export const getCountdown = (totalCountdown) => {
  let countDown = []
  for (let i = 0; i < totalCountdown; i++) {
    countDown.push({ label: `Countdown ${i + 1}`, value: i })
  }
  return countDown
}

export const getPushButtons = (totalPushButtons) => {
  let pushButtons = []
  for (let i = 0; i < totalPushButtons; i++) {
    pushButtons.push({ label: `Push Button ${i + 1}`, value: i })
  }
  return pushButtons
}

export const getPhases = (
  totalPhases,
  enabledPhase = ['1', '1', '1', '1', '1', '1', '1', '1', '1', '1']
) => {
  let phases = []
  for (let i = 0; i < totalPhases; i++) {
    if (enabledPhase[i] !== '0') {
      phases.push({ label: `Phase ${i + 1}`, value: i })
    }
  }
  return phases
}

export const getGoGreenSignal = (totalSignals) => {
  let signals = []
  for (let i = 0; i < totalSignals; i++) {
    signals.push({ label: `Signal ${i + 1}`, value: i })
  }
  return signals
}

export const fetchLatLong = (mapUrl) => {
  const pattern = /ll=(-?[\d.]+),(-?[\d.]+)/
  const match = mapUrl.match(pattern)

  return { lat: match[1], long: match[2] }
}

export const filterJunctionData = (tempMapData, xaitsLink) => {
  const junctionIds = [
    xaitsLink.junction1,
    xaitsLink.junction2,
    xaitsLink.junction3,
    xaitsLink.junction4,
  ].filter(Boolean)
  const matchingIds = []
  const filteredData = tempMapData.filter((data) => {
    if (junctionIds.includes(data.id)) {
      matchingIds.push(data)
      return false
    }
    return true
  })
  return { filteredData, matchingIds }
}
